import { gql, useQuery } from "@apollo/client";
import { BsFillPersonFill } from "react-icons/bs";

import { useMemo } from "react";
import { format } from "../../../core/money";
import { Product, ProductPrice } from "../../../graphql/operations";
import { getCurrency } from "../../../utils/getCurrency";
import Loader from "../Loader";
import { Text } from "../Typography";
import styles from "./Price.module.css";

type PriceProps = {
  id: Product["id"];
  price?: Pick<ProductPrice, "amount" | "currency" | "originalPrice">;
  size?: "small" | "normal" | "large";
  center?: boolean;
  showDiscount?: boolean;
};

export default function Price({
  id,
  price,
  size = "normal",
  center = true,
  showDiscount = true,
}: PriceProps) {
  const { loading, error, data } = useQuery<{ product: Product }>(
    GET_PRODUCT_PRICE,
    {
      variables: {
        productId: id,
        currency: getCurrency() || "USD",
      },
    }
  );

  const product = useMemo(() => data?.product, [data]);
  const mainPrice = useMemo(
    () => product?.mainPrice ?? price,
    [product?.mainPrice, price]
  );

  const discount = useMemo(() => {
    if (
      mainPrice?.amount === undefined ||
      mainPrice?.originalPrice === undefined
    )
      return 0;
    return mainPrice.originalPrice - mainPrice.amount;
  }, [mainPrice?.originalPrice, mainPrice?.amount]);

  const sizes = sizeLookup[size];

  if (loading) {
    return <Loader />;
  }

  if (error || !data || !data.product.mainPrice) {
    return <>error</>;
  }

  return (
    <>
      <div
        style={{
          ...(center && { textAlign: "center" }),
        }}
      >
        <div
          className={styles.MainPrice}
          style={{
            fontSize: sizes.price,
            ...(center && { justifyContent: "center" }),
          }}
        >
          <span>
            {product?.mainPrice.isGroup && "from "}

            {format({
              amount: Number(mainPrice.amount),
              currencyCode: mainPrice.currency,
            })}
          </span>{" "}
          / <BsFillPersonFill size={sizes.price} />
        </div>
        {showDiscount && discount > 0 && (
          <div>
            <Text
              muted
              style={{ textDecoration: "line-through" }}
              size={sizes.discount}
            >
              {format({
                amount: Number(mainPrice.originalPrice),
                currencyCode: mainPrice.currency,
              })}
            </Text>
            <Text color="accent" bold size={sizes.discount}>
              {` Save ${format({
                amount: Number(discount),
                currencyCode: mainPrice.currency,
              })}`}
            </Text>
          </div>
        )}
      </div>
    </>
  );
}

const GET_PRODUCT_PRICE = gql`
  query GetPrice($productId: ID!, $currency: Currency) {
    product(id: $productId) {
      id

      mainPrice(currency: $currency) {
        amount
        currency
        isGroup
        originalPrice
      }
    }
  }
`;

const sizeLookup = {
  small: {
    price: 14,
    discount: "smaller",
  },
  normal: {
    price: 20,
    discount: "smaller",
  },
  large: {
    price: 24,
    discount: "small",
  },
} as const;
